import React from 'react';
import { ImageAndTiles, SanityKeyed, SanityBlock } from 'types/studio/studio';
import { CustomPortableText } from 'shared/studio/PortableText';
import { Typography } from '@ag1/ui';
import clsx from 'clsx';
import styles from './NumbersSection.module.scss';

type Props = {
  numbersOptions: ImageAndTiles['numbersOptions'];
};

type NumbersWithHeadingsProps = {
  numbers: {
    _key: string;
    _type: 'numberData';
    number?: number | undefined;
    suffix?: string | undefined;
    description?: Array<SanityKeyed<SanityBlock>> | undefined;
    withPercentage?: boolean | undefined;
  }[];
  numberHeaders:
    | undefined
    | {
        _key: string;
        _type: 'header';
        headerContent: Array<SanityKeyed<SanityBlock>>;
      }[];
};

const NumbersWithHeadings = ({ numbers, numberHeaders }: NumbersWithHeadingsProps) => {
  const numberHeadersOrderCalc = (idx: number) => idx * 2 + 1;
  const numbersOrderCalc = (idx: number) => Math.floor(idx / 2) * 2 + (idx % 2 === 0 ? 1 : 2);

  return (
    <>
      {numberHeaders
        ? numberHeaders.map(({ headerContent, _key }, idx) => (
            <div
              key={_key}
              style={{ order: numberHeadersOrderCalc(idx) }}
              className={styles.headerItemFull}>
              <Typography variant="bodyMono" size="1" noPadding color="black-new">
                {typeof headerContent === 'string' ? (
                  headerContent
                ) : (
                  <CustomPortableText value={headerContent} />
                )}
              </Typography>
            </div>
          ))
        : ''}
      {numbers?.map((item, idx) => (
        <div key={item._key} className={styles.item} style={{ order: numbersOrderCalc(idx) }}>
          <Typography
            variant="headline"
            size="1"
            noPadding
            color="black-new"
            className={styles.number}>
            {item.number}
            {item.withPercentage ? '%' : ''}
            {item.suffix}
          </Typography>
          {item.description && (
            <Typography
              variant="portalText"
              size="2"
              noPadding
              color="black-new"
              weight="700"
              className={styles.description}>
              {typeof item.description === 'string' ? (
                item.description
              ) : (
                <CustomPortableText value={item.description} />
              )}
            </Typography>
          )}
        </div>
      ))}
    </>
  );
};

const NumbersSection = ({ numbersOptions }: Props) => {
  const { addNumbers, numbers, multipleHeaders, multipeHeader, singleHeader, headerBackground } =
    numbersOptions || {};

  if (!addNumbers && !Array.isArray(numbers)) {
    return null;
  }

  return (
    <div className={clsx(styles.root, singleHeader ? styles.noSpaceTop : '')}>
      {!multipeHeader && singleHeader ? (
        <Typography
          variant="bodyMono"
          size="1"
          noPadding
          color="black-new"
          weight="400"
          className={clsx(
            styles.headerFixedTopFull,
            headerBackground ? styles[headerBackground] : ''
          )}>
          <CustomPortableText value={singleHeader} />
        </Typography>
      ) : (
        ''
      )}
      <NumbersWithHeadings
        numberHeaders={multipeHeader ? multipleHeaders : []}
        numbers={numbers || []}
      />
    </div>
  );
};

export default NumbersSection;
